import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LayoutDashboard, Users, Store, Inbox, UtensilsCrossed, Download, LogOut, RefreshCw, Mail, Loader2 } from "lucide-react";
import { apiFetch } from "../components/admin/bits";
import { LoginCard } from "../components/admin/LoginCard";
import { DashboardView } from "../components/admin/DashboardView";
import { EnquiriesView } from "../components/admin/EnquiriesView";
import { SheetRecordsView } from "../components/admin/SheetRecordsView";
import { RestaurantsView } from "../components/admin/RestaurantsView";
import { ExportsView } from "../components/admin/ExportsView";

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "buyers", label: "Buyer Waitlist", icon: Users },
  { key: "vendors", label: "Vendor Applications", icon: Store },
  { key: "enquiries", label: "Contact Enquiries", icon: Inbox },
  { key: "restaurants", label: "Restaurant Sign-Ups", icon: UtensilsCrossed },
  { key: "exports", label: "Exports", icon: Download },
];

const TITLES = {
  dashboard: "Dashboard",
  buyers: "Buyer Waitlist",
  vendors: "Vendor Applications",
  enquiries: "Contact Enquiries",
  restaurants: "Restaurant Sign-Ups",
  exports: "Exports",
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [summary, setSummary] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadSummary = useCallback(async () => {
    try {
      const res = await apiFetch("/admin/summary");
      if (res.ok) setSummary(await res.json());
    } catch {
      // summary is non-critical; individual views surface their own errors
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/auth/me");
        setUser(res.ok ? await res.json() : false);
      } catch {
        setUser(false);
      }
    })();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    if (t && TITLES[t]) setTab(t);
    if (params.get("sheets") === "connected") {
      toast.success("Google Sheets connected.", { description: "Buyer and vendor records now load from your tracker sheets." });
    }
    if (params.toString()) window.history.replaceState({}, "", "/admin");
  }, []);

  useEffect(() => {
    if (user) loadSummary();
  }, [user, refreshKey, loadSummary]);

  const onLogout = async () => {
    await apiFetch("/auth/logout", { method: "POST" });
    setUser(false);
    setSummary(null);
  };

  const sendDigest = async () => {
    try {
      const res = await apiFetch("/admin/send-digest", { method: "POST" });
      if (!res.ok) throw new Error("send failed");
      toast.success("Weekly digest sent.", { description: "The summary email is on its way to aflimart.tech@gmail.com." });
    } catch {
      toast.error("Could not send the digest.");
    }
  };

  const onAuthFail = useCallback(() => setUser(false), []);

  return (
    <section data-testid="admin-page" className="relative mx-auto min-h-[80vh] max-w-6xl px-4 pb-24 pt-32 sm:px-8 sm:pt-40">
      {user === null ? (
        <div className="flex justify-center pt-24" data-testid="admin-loading"><Loader2 size={28} className="animate-spin text-[#1E3A8A]" /></div>
      ) : user === false ? (
        <LoginCard onLoggedIn={setUser} />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="eyebrow">Private · Admin</p>
              <h1 className="mt-4 font-sans text-3xl tracking-tight text-[#0F172A] sm:text-4xl" data-testid="admin-page-title">{TITLES[tab]}</h1>
              <p className="mt-2 text-sm text-[#94A3B8]">Signed in as {user.email}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={sendDigest} data-testid="admin-digest-button" className="btn-ghost !px-5 !py-2.5 !text-xs">
                <Mail size={14} /> Email Weekly Digest
              </button>
              <button onClick={() => setRefreshKey((k) => k + 1)} data-testid="admin-refresh-button" className="btn-ghost !px-5 !py-2.5 !text-xs">
                <RefreshCw size={14} /> Refresh
              </button>
              <button onClick={onLogout} data-testid="admin-logout-button" className="btn-ghost !px-5 !py-2.5 !text-xs">
                <LogOut size={14} /> Sign Out
              </button>
            </div>
          </div>

          <nav className="mt-10 flex gap-2 overflow-x-auto pb-2 sm:flex-wrap sm:overflow-visible" data-testid="admin-nav" aria-label="Admin sections">
            {NAV.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-nav-${t.key}`}
                className={`flex shrink-0 items-center gap-2 rounded-full border px-5 py-2.5 font-sans text-[11px] tracking-[0.2em] transition-[border-color,color,background-color] duration-300 ${
                  tab === t.key
                    ? "border-[#1E3A8A]/60 bg-[#1E3A8A]/10 text-[#1E3A8A]"
                    : "border-[#E2E8F0] text-[#64748B] hover:border-[#1E3A8A]/30 hover:text-[#475569]"
                }`}
              >
                <t.icon size={13} />
                {t.label.toUpperCase()}
              </button>
            ))}
          </nav>

          {tab === "dashboard" && <DashboardView summary={summary} onNavigate={setTab} />}
          {tab === "buyers" && (
            <SheetRecordsView
              testId="buyers"
              endpoint="/admin/buyers"
              exportPath="/admin/export/buyers"
              exportFile="aflimart-buyer-waitlist.csv"
              emptyLabel="No buyer waitlist records yet"
              followupNoun="early-access sign-up"
              refreshKey={refreshKey}
              onAuthFail={onAuthFail}
            />
          )}
          {tab === "vendors" && (
            <SheetRecordsView
              testId="vendors"
              endpoint="/admin/vendors"
              exportPath="/admin/export/vendors"
              exportFile="aflimart-vendor-applications.csv"
              emptyLabel="No vendor applications yet"
              followupNoun="vendor application"
              refreshKey={refreshKey}
              onAuthFail={onAuthFail}
            />
          )}
          {tab === "enquiries" && <EnquiriesView refreshKey={refreshKey} onAuthFail={onAuthFail} />}
          {tab === "restaurants" && <RestaurantsView refreshKey={refreshKey} onAuthFail={onAuthFail} />}
          {tab === "exports" && <ExportsView summary={summary} />}
        </>
      )}
    </section>
  );
}
